import { NegotiationOfferStatus } from '@interfaces/models/negotiation/domain/NegotiationOfferStatus';
import { Negotiation } from '@interfaces/models/negotiation/negotiation';

export enum OfferStatusId {
  sent = 'sent',
  received = 'received',
  accepted = 'accepted',
  declined = 'declined',
  timeout = 'timeout',
  external = 'external',
  order = 'order',
}

interface OfferStatusUI {
  id: OfferStatusId;
  icon: string;
  isNegoPriceShown: boolean;
  isYouEarnShown: boolean;
}

export interface OfferStatus extends OfferStatusUI {
  message: string;
  price: string;
  sellerPrice: string;
  subMessage: string;
  isLastOffer: boolean;
  isSeller: boolean;
  canBuyerRespond: boolean;
}

type OfferStatusMapType = {
  [key in OfferStatusId]: OfferStatusUI;
};

const offerStatusMap: OfferStatusMapType = {
  [OfferStatusId.sent]: {
    id: OfferStatusId.sent,
    icon: 'chrono-default',
    isNegoPriceShown: true,
    isYouEarnShown: true,
  },
  [OfferStatusId.received]: {
    id: OfferStatusId.received,
    icon: 'chrono-default',
    isNegoPriceShown: true,
    isYouEarnShown: true,
  },
  [OfferStatusId.accepted]: {
    id: OfferStatusId.accepted,
    icon: 'tick-small-default',
    isNegoPriceShown: true,
    isYouEarnShown: true,
  },
  [OfferStatusId.declined]: {
    id: OfferStatusId.declined,
    icon: 'close-default',
    isNegoPriceShown: true,
    isYouEarnShown: true,
  },
  [OfferStatusId.timeout]: {
    id: OfferStatusId.timeout,
    icon: 'chrono-timeout-default',
    isNegoPriceShown: false,
    isYouEarnShown: false,
  },
  [OfferStatusId.external]: {
    id: OfferStatusId.external,
    icon: 'chrono-timeout-default',
    isNegoPriceShown: false,
    isYouEarnShown: false,
  },
  [OfferStatusId.order]: {
    id: OfferStatusId.order,
    icon: 'tick-small-default',
    isNegoPriceShown: true,
    isYouEarnShown: false,
  },
};

export const getOfferType = (negotiation: NegotiationOfferStatus | Negotiation, isSeller: boolean): OfferStatusId => {
  if (!negotiation.lastEvent) {
    return OfferStatusId.sent;
  }
  if (
    !negotiation.isClosed &&
    negotiation.lastEvent.action === 'offer' &&
    ((isSeller && negotiation.lastEvent.createdBy === 'buyer') ||
      (!isSeller && negotiation.lastEvent.createdBy === 'seller'))
  ) {
    return OfferStatusId.received;
  } else if (
    !negotiation.isClosed &&
    negotiation.lastEvent.action === 'offer' &&
    ((isSeller && negotiation.lastEvent.createdBy === 'seller') ||
      (!isSeller && negotiation.lastEvent.createdBy === 'buyer'))
  ) {
    return OfferStatusId.sent;
  } else if (negotiation.lastEvent.action === 'accept' && negotiation.isClosed === false) {
    return OfferStatusId.accepted;
  } else if (negotiation.lastEvent.action === 'reject') {
    if (negotiation.nextActions && negotiation.nextActions.length > 0) {
      return OfferStatusId.received;
    }
    return OfferStatusId.declined;
  } else if (
    (negotiation.lastEvent.action === 'offer' && negotiation.isClosed === true) ||
    (negotiation.lastEvent.action === 'accept' && negotiation.isClosed === true) ||
    negotiation.lastEvent.action === 'timeoutAccept' ||
    negotiation.lastEvent.action === 'timeoutOffer'
  ) {
    return OfferStatusId.timeout;
  } else if (negotiation.lastEvent.action === 'external') {
    return OfferStatusId.external;
  } else if (negotiation.lastEvent.action === 'order') {
    return OfferStatusId.order;
  } else {
    return OfferStatusId.sent;
  }
};

export const useGetNegotiationStatus = (
  negotiation: NegotiationOfferStatus,
  isSeller: boolean,
  isLastOffer: boolean,
) => {
  const offerType = getOfferType(negotiation, isSeller);

  const offerStatus: OfferStatus = {
    ...offerStatusMap[offerType],
    message: negotiation.lastEvent.userMessage,
    price: negotiation.lastEvent.price?.formatted,
    sellerPrice: negotiation.lastEvent.sellerPrice?.formatted,
    subMessage: negotiation.userSubMessage,
    isLastOffer,
    isSeller,
    canBuyerRespond: true,
  };
  return offerStatus;
};
